import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  Bell,
  User,
  HelpCircle,
  Settings,
  LogOut,
  Shield,
  ChevronDown,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react';
import PropertySelector from './PropertySelector';
import NotificationsPanel from './NotificationsPanel';
import { useUser } from '../app/providers/UserContext';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useProperty, usePricing } from '../app/providers';

interface HeaderProps {
  title: string;
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const { selectedProperty } = useProperty();
  const { alerts } = usePricing();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsPanelOpen, setIsNotificationsPanelOpen] =
    useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);

  const unreadCount = selectedProperty
    ? alerts.filter((a) => a.propertyId === selectedProperty.id && !a.read)
        .length
    : 0;

  const userInitials =
    user?.name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';

  const handleSignOut = async () => {
    // Close dropdown first to prevent UI issues
    setIsProfileOpen(false);

    try {
      await signOut();
      // Force navigation after a small delay to ensure state is cleared
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 100);
    } catch (err) {
      console.error('Error signing out:', err);
      // Navigate anyway on error - user wants to leave
      navigate('/login', { replace: true });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsProfileOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (
        event.key === 'Escape' &&
        dropdownRef.current?.contains(document.activeElement)
      ) {
        setIsProfileOpen(false);
        accountButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const getRoleBadgeColor = (role?: string) => {
    switch (role) {
      case 'Admin':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      case 'Revenue Manager':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'Manager':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      default:
        return 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:bg-slate-700 dark:text-slate-300';
    }
  };

  return (
    <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-2 sm:gap-x-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 dark:bg-slate-900 dark:border-slate-800 px-3 sm:px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
      <button
        type="button"
        className="shrink-0 p-3 text-gray-700 dark:text-slate-300 md:hidden hover:bg-gray-100 dark:hover:bg-slate-700 dark:hover:bg-slate-800 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
        onClick={onMenuClick}
        aria-label="Open sidebar"
      >
        <Menu className="h-6 w-6" />
      </button>

      <div className="flex-1 min-w-0 max-w-md">
        <PropertySelector />
      </div>

      <div className="flex shrink-0 items-center gap-x-1 lg:gap-x-6">
        <div className="flex items-center gap-x-1 sm:gap-x-2">
          <button
            type="button"
            className="hidden sm:flex relative rounded-lg p-3 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 dark:hover:bg-slate-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            title="Help & Support"
            aria-label="Help and workflow guides"
            onClick={() => navigate('/help')}
          >
            <HelpCircle className="h-5 w-5" />
          </button>

          <button
            type="button"
            onClick={() => setIsNotificationsPanelOpen(true)}
            className="relative rounded-lg p-3 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 dark:hover:bg-slate-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            title="Notifications"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-xs text-white flex items-center justify-center font-medium">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center gap-x-2 rounded-lg p-1.5 sm:p-2 text-sm font-semibold text-gray-900 dark:text-slate-100 hover:bg-gray-100 dark:hover:bg-slate-700 dark:hover:bg-slate-800 transition-colors min-h-[44px] min-w-[44px]"
              ref={accountButtonRef}
              title="Account menu"
              aria-label="Account menu"
              aria-expanded={isProfileOpen}
              aria-controls="account-menu"
            >
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name || 'User'}
                  className="h-9 w-9 sm:h-8 sm:w-8 rounded-full object-cover ring-2 ring-gray-200 dark:ring-slate-700"
                />
              ) : (
                <div className="h-9 w-9 sm:h-8 sm:w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm ring-2 ring-gray-200 dark:ring-slate-700">
                  <span className="text-sm font-semibold text-white">
                    {userInitials}
                  </span>
                </div>
              )}
              <div className="hidden lg:flex lg:flex-col lg:items-start max-w-48 min-w-0">
                <span className="max-w-full truncate text-sm font-medium text-gray-900 dark:text-slate-100">
                  {user?.name || 'Guest'}
                </span>
                {user?.role && (
                  <span className="text-xs text-gray-500 dark:text-slate-400">
                    {user.role}
                  </span>
                )}
              </div>
              <ChevronDown
                className={`hidden lg:block h-4 w-4 text-gray-400 transition-transform ${
                  isProfileOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isProfileOpen && (
              <div
                id="account-menu"
                className="fixed top-[4.5rem] left-3 right-3 sm:left-auto sm:absolute sm:top-full sm:mt-2 sm:w-72 max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain origin-top-right rounded-xl bg-white dark:bg-slate-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none"
              >
                <div className="p-4 border-b border-gray-100 dark:border-slate-800 dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    {user?.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name || 'User'}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <span className="text-lg font-semibold text-white">
                          {userInitials}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-slate-100 truncate">
                        {user?.name || 'Guest User'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                        {user?.email || 'Not signed in'}
                      </p>
                      {user?.role && (
                        <span
                          className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}
                        >
                          <Shield className="w-3 h-3 mr-1" />
                          {user.role}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="py-1">
                  <button
                    onClick={() => {
                      setIsProfileOpen(false);
                      navigate('/settings?section=account');
                    }}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <User className="h-4 w-4 text-gray-400" />
                    <span>Account details</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsProfileOpen(false);
                      navigate('/settings');
                    }}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <Settings className="h-4 w-4 text-gray-400" />
                    <span>Settings</span>
                  </button>
                </div>

                <div className="border-t border-gray-100 dark:border-slate-800 dark:border-slate-700 py-2 px-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
                    Theme
                  </p>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setTheme('light')}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        theme === 'light'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                      }`}
                      title="Light theme"
                    >
                      <Sun className="h-4 w-4" />
                      <span className="hidden sm:inline">Light</span>
                    </button>
                    <button
                      onClick={() => setTheme('dark')}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        theme === 'dark'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                      }`}
                      title="Dark theme"
                    >
                      <Moon className="h-4 w-4" />
                      <span className="hidden sm:inline">Dark</span>
                    </button>
                    <button
                      onClick={() => setTheme('system')}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        theme === 'system'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                      }`}
                      title="System theme"
                    >
                      <Monitor className="h-4 w-4" />
                      <span className="hidden sm:inline">System</span>
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-100 dark:border-slate-800 dark:border-slate-700 py-1">
                  <button
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <NotificationsPanel
        isOpen={isNotificationsPanelOpen}
        onClose={() => setIsNotificationsPanelOpen(false)}
      />
    </div>
  );
};

export default Header;
